// @ts-nocheck
import _ from 'lodash';
import axios from 'axios';
import { create } from 'zustand';
import { API_URL } from '../../configs';
import { toastTypes } from '../../enums';
import getRoot from '../root';

const useGoogleTagManagerStore = create((set, get) => ({
  configs: { enabled: false },
  accounts: [],
  selected: { accountIdx: null, containerIdx: null, workspaceIdx: null },
  loading: {
    getAuthUrl: false, getConfigs: false, updateConfigs: false, setup: false,
    getAllResources: true, createWorkspace: false, installCaptureTag: false, publishWorkspace: false,
  },
  authUrl: '',
  inputConfigs: { enabled: false },

  anyLoading: () => _.includes(get().loading, true),

  setConfigField: (name, value) => set((s) => ({ configs: { ...s.configs, [name]: value } })),

  reset: () => set({
    configs: { enabled: false },
    accounts: [],
    loading: _.mapValues(get().loading, () => false),
  }),

  handleSelect: (_e, el) => {
    const value = el.type === 'checkbox' ? el.checked : el.value;
    set((s) => ({ selected: { ...s.selected, [el.name]: value } }));
  },

  resetInputConfigs: () => set((s) => ({ inputConfigs: s.configs })),

  getAuthUrl: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/GoogleTagManager/auth-url`);
      if (res.data.success) {
        set({ authUrl: res.data.authUrl });
        return res.data.authUrl;
      }
    } catch (e) { /* ignore */ }
    return null;
  },

  openAuthPage: async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/GoogleTagManager/auth-url`);
      if (res.data.success) window.open(res.data.authUrl, '_blank');
    } catch (e) { /* ignore */ }
  },

  getConfigs: async () => {
    try {
      set((s) => ({ loading: { ...s.loading, getConfigs: true } }));
      const { data } = await axios.get(`${API_URL}/api/integrations/GoogleTagManager/config`);
      if (data.success) set((s) => ({ configs: { ...s.configs, ...data.config } }));
    } catch (e) { /* ignore */ } finally {
      set((s) => ({ loading: { ...s.loading, getConfigs: false } }));
    }
  },

  updateConfigs: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, updateConfigs: true } }));
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleTagManager/config`, { ...get().configs });
      if (data.success) {
        root.success('Successfully updated Google Tag Manager configurations', toastTypes.googleTagManagerSetUp);
        root.clearToast(toastTypes.googleTagManagerSetUp);
      } else throw new Error();
    } catch (e) {
      root.failure('Something went wrong...', toastTypes.googleTagManagerSetUp);
    } finally {
      get().getConfigs();
      set((s) => ({ loading: { ...s.loading, updateConfigs: false } }));
    }
  },

  setup: async (code, scope) => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, setup: true } }));
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleTagManager/setup`, { code, scope });
      if (data.success) {
        get().getAllResources();
        root.success('Google Tag Manager integration successful!');
      } else throw new Error();
    } catch (e) {
      root.failure('Google Tag Manager integration unsuccessful.');
    } finally {
      set((s) => ({ loading: { ...s.loading, setup: false } }));
      get().getConfigs();
    }
  },

  getAllResources: async () => {
    try {
      set((s) => ({ loading: { ...s.loading, getAllResources: true } }));
      const { data } = await axios.get(`${API_URL}/api/integrations/GoogleTagManager/resources`);
      if (!data.success) throw new Error('Failed to get GTM resources...');
      set({ accounts: data.accounts });
    } catch (err) { /* ignore */ }
    set((s) => ({ loading: { ...s.loading, getAllResources: false } }));
  },

  createWorkspace: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, createWorkspace: true } }));
      const { accountIdx, containerIdx } = get().selected;
      const container = _.get(get().accounts, [accountIdx, 'containers', containerIdx]);
      if (!container) throw new Error('You must specify a container!');
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleTagManager/${container.path}/workspaces`);
      if (data.success) {
        get().getAllResources();
        root.success('Successfully created workspace!', toastTypes.createWorkspace);
        root.clearToast(toastTypes.createWorkspace);
      } else throw new Error('Could not create workspace...');
    } catch (err) {
      root.failure(err.message, toastTypes.createWorkspace);
    }
    set((s) => ({ loading: { ...s.loading, createWorkspace: false } }));
  },

  addCaptureTag: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, installCaptureTag: true } }));
      const { accountIdx, containerIdx, workspaceIdx } = get().selected;
      const ws = _.get(get().accounts, [accountIdx, 'containers', containerIdx, 'workspaces', workspaceIdx]);
      if (!ws) throw new Error('You must specify a workspace!');
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleTagManager/${ws.path}/tags`);
      if (data.success) {
        get().getAllResources();
        root.success('Successfully added Capture tag!', toastTypes.addCaptureTag);
        root.clearToast(toastTypes.addCaptureTag);
      } else throw new Error('Could not add capture tag...');
    } catch (err) {
      root.failure(err.message, toastTypes.addCaptureTag);
    }
    set((s) => ({ loading: { ...s.loading, installCaptureTag: false } }));
  },

  publishWorkspace: async () => {
    const root = getRoot();
    try {
      set((s) => ({ loading: { ...s.loading, publishWorkspace: true } }));
      const { accountIdx, containerIdx, workspaceIdx } = get().selected;
      const ws = _.get(get().accounts, [accountIdx, 'containers', containerIdx, 'workspaces', workspaceIdx]);
      if (!ws) throw new Error('You must specify a workspace!');
      const { data } = await axios.post(`${API_URL}/api/integrations/GoogleTagManager/${ws.path}/publish`);
      if (data.success) {
        get().getAllResources();
        root.success('Successfully published your workspace!', toastTypes.publishWorkspace);
        root.clearToast(toastTypes.publishWorkspace);
      } else throw new Error('Could not publish your workspace...');
    } catch (err) {
      root.failure(err.message, toastTypes.publishWorkspace);
    }
    set((s) => ({ loading: { ...s.loading, publishWorkspace: false } }));
  },
}));

export default useGoogleTagManagerStore;
